import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useAppStore } from "@/global/store";
import {
  selectSections,
  selectSectionSelected,
} from "@/global/store/data/gameFilters";
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Divider,
  Stack,
  Theme,
  Typography,
  lighten,
  useTheme,
} from "@mui/material";
import SectionCardImage from "public/images/section_details_card_bg.png";
import { findMappedSection } from "./utils.seatMap";

interface Props {
  onClickView3d?: () => void;
  onClickShowSeats?: () => void;
  sectionName?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export function SectionDetailsCard(props: Props) {
  const teamConfig = useTeamConfig();
  const { images, seatMapConfigs } = teamConfig;
  const {
    isView3dButtonHidden,
    isShowAvailableSeatsButtonHidden,
    isView3dButtonHiddenBySectionName,
  } = seatMapConfigs;
  const theme = useTheme();
  const styles = useStyles(theme);
  const tooltipSection = props?.sectionName?.replace("S_", "");
  const selectedSection = useAppStore(selectSectionSelected);
  const availableSections = useAppStore(selectSections);
  const apiSectionName = availableSections?.find((itm) => {
    const section = findMappedSection(teamConfig, itm);
    return section?.mapSectionName === tooltipSection;
  });

  const isView3dButtonHiddenForSection =
    isView3dButtonHiddenBySectionName?.includes(
      apiSectionName?.displaySectionName,
    ) || false;

  return (
    <Stack
      direction={"column"}
      sx={styles.root}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
    >
      {!(isView3dButtonHidden || isView3dButtonHiddenForSection) ? (
        <Box
          sx={styles.imageWrapper}
          onClick={() => {
            if (isView3dButtonHidden || isView3dButtonHiddenForSection) return;
            props?.onClickView3d?.();
          }}
        >
          <ButtonBase sx={styles.ripple} />
          <Box sx={styles.imageTextOverlay}>
            <Box
              className="section-details-card-image-text-wrapper"
              sx={styles.imageTextWrapper}
            >
              <Typography
                variant="body1"
                color={theme.palette.common.white}
                fontWeight={600}
                fontSize={"14px"}
              >
                Click to View 3d
              </Typography>
            </Box>
          </Box>
          <Box
            className="section-details-card-image"
            component={"img"}
            sx={styles.image}
            src={images.seatMap3dPreviewImage}
            title="section image"
          />
        </Box>
      ) : null}

      <Box sx={styles.contentWrapper}>
        <Stack
          direction={"row"}
          p={2}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Typography variant="body1" color="text.primary" fontWeight={600}>
            Section
          </Typography>
          <Box
            sx={{
              backgroundColor: lighten(theme.palette.primary.main, 0.9),
              padding: "2px 6px",
              borderRadius: "4px",
            }}
          >
            <Typography
              variant="body1"
              color={theme.palette.primary.main}
              fontWeight={600}
            >
              {apiSectionName?.displaySectionName}
            </Typography>
          </Box>
        </Stack>

        {!isShowAvailableSeatsButtonHidden ? (
          <>
            <Divider />
            <Stack p={2}>
              <Button
                disabled={
                  apiSectionName?.mapSectionName ===
                  selectedSection?.mapSectionName
                }
                onClick={props?.onClickShowSeats}
                size="small"
                variant="contained"
                sx={{
                  width: "fit-content",
                  borderRadius: "6px",
                }}
              >
                Show Available Seats
              </Button>
            </Stack>
          </>
        ) : null}
      </Box>
    </Stack>
  );
}

function useStyles(theme: Theme) {
  return {
    root: {
      borderRadius: "8px 8px 8px 8px",
      width: "300px",
      overflow: "hidden",
      boxShadow: "0 0 8px 2px rgba(0,0,0,0.1)",
      position: "relative",
      transition: "all 0.2s ease",
      zIndex: "9999",

      "&:hover": {
        boxShadow: "0 0 16px 4px rgba(0,0,0,0.2)",
      },
    },
    ripple: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      zIndex: 2,
    },
    imageWrapper: {
      width: "100%",
      height: "150px",
      overflow: "hidden",
      position: "relative",
      cursor: "pointer",
      "&:hover ": {
        "& .section-details-card-image": {
          transform: "scale(1.3)",
        },
        "& .section-details-card-image-text-wrapper": {
          transform: "scale(1.1)",
        },
      },
    },
    imageTextOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "rgba(0,0,0,0.5)",
      zIndex: "1",
    },
    imageTextWrapper: {
      padding: "8px 16px",
      border: `1px solid ${theme.palette.common.white}`,
      borderRadius: "24px",
      transition: "all 0.2s ease",
    },
    image: {
      width: "100%",
      height: "auto",
      objectFit: "cover",
      transition: "all 0.2s ease",
    },
    contentWrapper: {
      border: `1px solid ${theme.palette.divider}`,
      borderTop: "none",
      borderRadius: "0 0 8px 8px",
      backgroundColor: theme.palette.background.paper,
    },
  };
}
